import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gray-50" }
const _hoisted_2 = {
  key: 0,
  class: "flex items-center justify-center min-h-screen"
}
const _hoisted_3 = {
  key: 1,
  class: "max-w-2xl mx-auto px-6 py-16 text-center"
}
const _hoisted_4 = { class: "bg-white rounded-lg shadow-sm border border-gray-200 p-8" }
const _hoisted_5 = { class: "text-2xl font-bold text-gray-900 mb-4" }
const _hoisted_6 = { class: "text-gray-600 mb-6" }
const _hoisted_7 = {
  key: 2,
  class: "w-full px-6 py-8"
}
const _hoisted_8 = { class: "bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8 text-center" }
const _hoisted_9 = { class: "space-y-3 mb-6" }
const _hoisted_10 = { class: "text-lg" }
const _hoisted_11 = { class: "font-semibold" }
const _hoisted_12 = { class: "text-gray-500 ml-1" }
const _hoisted_13 = { class: "flex flex-col sm:flex-row gap-3 justify-center" }
const _hoisted_14 = { class: "space-y-6" }
const _hoisted_15 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_16 = { class: "text-base font-semibold text-gray-700 mb-4" }
const _hoisted_17 = { class: "flex flex-col sm:flex-row gap-3 justify-center mt-8" }
const _hoisted_18 = {
  key: 3,
  class: "w-full px-6 py-8"
}
const _hoisted_19 = { class: "bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8" }
const _hoisted_20 = { class: "flex items-center justify-between mb-4" }
const _hoisted_21 = { class: "text-2xl font-bold text-gray-900" }
const _hoisted_22 = { class: "text-right" }
const _hoisted_23 = { class: "text-sm text-gray-500" }
const _hoisted_24 = {
  key: 0,
  class: "space-y-8 mb-8"
}
const _hoisted_25 = { class: "mb-4" }
const _hoisted_26 = { class: "text-lg font-semibold text-gray-900" }
const _hoisted_27 = { class: "flex justify-center" }

import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getTranslatedString as _ } from "@/i18n";
import { useProgressStore } from "@/stores/progressStore";
import { usePublicCourseStore } from "@/stores/publicCourseStore";
import type { EventParticipation, EventParticipationSlot } from "@/models/interfaces";
import { submitTopicQuiz } from "@/api/courses";
import Spinner from "@/components/ui/Spinner.vue";
import Btn from "@/components/ui/Btn.vue";
import AbstractEventParticipationSlot from "@/components/shared/AbstractEventParticipationSlot.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'PublicTopicQuiz',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const progressStore = useProgressStore();
const publicCourseStore = usePublicCourseStore();

const quiz = ref<EventParticipation | null>(null);
const quizResults = ref<EventParticipation | null>(null);
const loading = ref(true);
const submitting = ref(false);
const retrying = ref(false);
const quizCompleted = ref(false);

const timeLeft = ref<number | null>(null);
const timerInterval = ref<number | null>(null);

const slug = route.params.slug as string;
const topicId = route.params.topicId as string;

const quizSlots = computed((): EventParticipationSlot[] => quiz.value?.slots ?? []);
const totalQuestions = computed(() => quizSlots.value.length);

const topicName = computed(() => {
	const topics = publicCourseStore.currentCourseContent;
	// topic ids come from serialized content (numbers) while topicId is the
	// string route param, so normalize before comparing.
	const topic = (topics as any[]).find(t => String(t.id) === String(topicId));
	return topic?.name || "";
});

const courseId = computed(() => publicCourseStore.courseId ?? "");

// Scores arrive from the backend as strings, so parse them as floats before
// doing any arithmetic (otherwise `+` concatenates instead of adding).
const resultScore = computed(
	() => parseFloat((quizResults.value as any)?.score ?? "0") || 0,
);
const resultMaxScore = computed(() =>
	(quizResults.value?.slots ?? []).reduce(
		(sum, s) => sum + (parseFloat(String(s.weight)) || 1),
		0,
	),
);
const resultScorePercentage = computed(() =>
	resultMaxScore.value > 0 ? (resultScore.value / resultMaxScore.value) * 100 : 0,
);
const resultPassed = computed(() => resultScorePercentage.value >= 70);

onMounted(async () => {
	try {
		const courseData = await publicCourseStore.loadPublicCourse(slug);
		publicCourseStore.enrolledCourse = courseData.course;

		if (publicCourseStore.courseId) {
			// Ensure course content is loaded so the topic name resolves in the
			// title even when the quiz is opened directly via its URL.
			if (!publicCourseStore.currentCourseContent.length) {
				await publicCourseStore.loadCourseContent(publicCourseStore.courseId);
			}

			quiz.value = (await progressStore.createTopicQuiz(
				publicCourseStore.courseId,
				topicId,
				{ question_count: 2, difficulty_level: "medium" },
			)) as unknown as EventParticipation;

			if ((quiz.value as any)?.time_limit_seconds) {
				timeLeft.value = (quiz.value as any).time_limit_seconds;
				startTimer();
			}
		}
	} catch (err) {
		console.error("Failed to load quiz:", err);
	} finally {
		loading.value = false;
	}
});

onUnmounted(() => {
	if (timerInterval.value) clearInterval(timerInterval.value);
});

const startTimer = () => {
	if (timerInterval.value) clearInterval(timerInterval.value);
	timerInterval.value = window.setInterval(() => {
		if (timeLeft.value !== null && timeLeft.value > 0) {
			timeLeft.value--;
		} else {
			if (timerInterval.value) clearInterval(timerInterval.value);
			submitQuiz();
		}
	}, 1000);
};

const formatTime = (seconds: number): string => {
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;
	return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const onUpdateSubmission = (event: {
	slot: EventParticipationSlot;
	payload: [string, any];
}) => {
	// event.slot is the actual changed slot (may be a sub-slot for AGGREGATED exercises)
	const [field, value] = event.payload;
	(event.slot as any)[field] = value;
};

const handleSubmitClick = async () => {
	const hasAnswers = quizSlots.value.some(
		slot =>
			slot.selected_choices?.length > 0 ||
			slot.answer_text?.trim() ||
			slot.execution_results ||
			slot.attachment,
	);
	const confirmMessage = hasAnswers
		? _("learning_journey.quiz_confirm_submit")
		: _("learning_journey.quiz_confirm_submit_empty");
	if (window.confirm(confirmMessage)) {
		await submitQuiz();
	}
};

const collectAllSlots = (slots: EventParticipationSlot[]): EventParticipationSlot[] =>
	slots.flatMap(slot => [slot, ...collectAllSlots(slot.sub_slots ?? [])]);

const submitQuiz = async () => {
	submitting.value = true;
	try {
		const slots = collectAllSlots(quizSlots.value).map(slot => ({
			id: slot.id,
			selected_choices: slot.selected_choices ?? [],
			answer_text: slot.answer_text ?? "",
		}));

		quizResults.value = await submitTopicQuiz(courseId.value, topicId, slots);

		quizCompleted.value = true;

		if (timerInterval.value) {
			clearInterval(timerInterval.value);
			timerInterval.value = null;
		}

		if (resultPassed.value && publicCourseStore.courseId) {
			await progressStore.markLessonComplete(publicCourseStore.courseId, topicId);
		}
	} catch (err) {
		console.error("Failed to submit quiz:", err);
	} finally {
		submitting.value = false;
	}
};

const retryQuiz = async () => {
	if (!publicCourseStore.courseId) return;
	retrying.value = true;
	try {
		quizCompleted.value = false;
		quizResults.value = null;

		if (timerInterval.value) {
			clearInterval(timerInterval.value);
			timerInterval.value = null;
		}

		// Backend returns a fresh participation when the previous one is TURNED_IN
		quiz.value = (await progressStore.createTopicQuiz(
			publicCourseStore.courseId,
			topicId,
			{ question_count: 2, difficulty_level: "medium" },
		)) as unknown as EventParticipation;

		if ((quiz.value as any)?.time_limit_seconds) {
			timeLeft.value = (quiz.value as any).time_limit_seconds;
			startTimer();
		} else {
			timeLeft.value = null;
		}
	} catch (err) {
		console.error("Failed to start new quiz attempt:", err);
	} finally {
		retrying.value = false;
	}
};

const goBackToJourney = () => {
	router.push(`/c/${slug}/journey`);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(Spinner, {
            size: "xl",
            variant: "primary"
          })
        ]))
      : (!quiz.value || _unref(totalQuestions) === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("h1", _hoisted_5, _toDisplayString(_ctx.$t("learning_journey.quiz_not_available")), 1),
              _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t("learning_journey.quiz_no_content")), 1),
              _createVNode(Btn, {
                onClick: goBackToJourney,
                variant: "primary"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t("learning_journey.back_to_journey")), 1)
                ]),
                _: 1
              })
            ])
          ]))
        : (quizCompleted.value && quizResults.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("span", _hoisted_11, _toDisplayString(_ctx.$t("learning_journey.quiz_score")) + ":", 1),
                    _createElementVNode("span", {
                      class: _normalizeClass([_unref(resultPassed) ? 'text-green-600' : 'text-red-600', "font-bold ml-1"])
                    }, _toDisplayString(Math.round(_unref(resultScorePercentage))) + "% ", 3),
                    _createElementVNode("span", _hoisted_12, " (" + _toDisplayString(_unref(resultScore)) + " / " + _toDisplayString(_unref(resultMaxScore)) + " pts) ", 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_13, [
                  _createVNode(Btn, {
                    onClick: retryQuiz,
                    variant: "outline",
                    loading: retrying.value
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t("learning_journey.quiz_try_again")), 1)
                    ]),
                    _: 1
                  }, 8, ["loading"]),
                  _createVNode(Btn, {
                    onClick: goBackToJourney,
                    variant: "primary"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t("learning_journey.quiz_continue_learning")), 1)
                    ]),
                    _: 1
                  })
                ])
              ]),
              _createElementVNode("div", _hoisted_14, [
                _createElementVNode("h2", _hoisted_15, _toDisplayString(_ctx.$t("learning_journey.quiz_review_answers")), 1),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(quizResults.value.slots, (slot, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: slot.id,
                    class: "bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                  }, [
                    _createElementVNode("h3", _hoisted_16, _toDisplayString(_ctx.$t("learning_journey.quiz_question", { n: index + 1 })), 1),
                    _createVNode(AbstractEventParticipationSlot, {
                      modelValue: slot,
                      allowEditSubmission: false,
                      showAnswer: true,
                      showSolution: true,
                      showAssessmentCard: true,
                      showTags: false,
                      showHintButton: false
                    }, null, 8, ["modelValue"])
                  ]))
                }), 128))
              ]),
              _createElementVNode("div", _hoisted_17, [
                _createVNode(Btn, {
                  onClick: retryQuiz,
                  variant: "outline",
                  loading: retrying.value
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("learning_journey.quiz_try_again")), 1)
                  ]),
                  _: 1
                }, 8, ["loading"]),
                _createVNode(Btn, {
                  onClick: goBackToJourney,
                  variant: "primary"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t("learning_journey.quiz_continue_learning")), 1)
                  ]),
                  _: 1
                })
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_18, [
              _createElementVNode("div", _hoisted_19, [
                _createElementVNode("div", _hoisted_20, [
                  _createElementVNode("div", null, [
                    _createElementVNode("h1", _hoisted_21, _toDisplayString(_ctx.$t("learning_journey.quiz_review_title", { topic: _unref(topicName) })), 1)
                  ]),
                  _createElementVNode("div", _hoisted_22, [
                    _createElementVNode("div", _hoisted_23, _toDisplayString(_ctx.$t("learning_journey.quiz_questions_count", { n: _unref(totalQuestions) })), 1),
                    (timeLeft.value !== null)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: _normalizeClass(["text-lg font-semibold", timeLeft.value < 60 ? 'text-red-600' : 'text-blue-600'])
                        }, _toDisplayString(formatTime(timeLeft.value)), 3))
                      : _createCommentVNode("", true)
                  ])
                ])
              ]),
              (_unref(quizSlots).length > 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(quizSlots), (slot, index) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: slot.id,
                        class: "bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                      }, [
                        _createElementVNode("div", _hoisted_25, [
                          _createElementVNode("h3", _hoisted_26, _toDisplayString(_ctx.$t("learning_journey.quiz_question", { n: index + 1 })), 1)
                        ]),
                        _createVNode(AbstractEventParticipationSlot, {
                          modelValue: slot,
                          onUpdateSubmission: _cache[0] || (_cache[0] = ($event: any) => (onUpdateSubmission($event))),
                          allowEditSubmission: true,
                          showAnswer: false,
                          showSolution: false,
                          showTags: false,
                          showHintButton: false,
                          showAssessmentCard: false
                        }, null, 8, ["modelValue"])
                      ]))
                    }), 128))
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_27, [
                _createVNode(Btn, {
                  onClick: handleSubmitClick,
                  variant: "primary",
                  size: "lg",
                  loading: submitting.value,
                  class: "px-8 py-3"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(submitting.value
							? _ctx.$t("learning_journey.quiz_submitting")
							: _ctx.$t("learning_journey.quiz_submit")), 1)
                  ]),
                  _: 1
                }, 8, ["loading"])
              ])
            ]))
  ]))
}
}

})